class LoadMoreProductGrid extends HTMLElement {
  #abortController = new AbortController();
  #isLoading = false;
  #productJsonCache = {};

  connectedCallback() {
    const loadMoreButton = this.querySelector('[ref="loadMoreButton"]');
    if (loadMoreButton) {
      loadMoreButton.addEventListener("click", this.#handleLoadMore.bind(this), {
        signal: this.#abortController.signal,
      });
    }
  }

  disconnectedCallback() {
    this.#abortController.abort();
  }

  #getProductJson(handle) {
    if (!handle) return Promise.resolve(null);
    if (this.#productJsonCache[handle]) return Promise.resolve(this.#productJsonCache[handle]);
    return fetch("/products/" + handle + ".js")
      .then((res) => (res.ok ? res.json() : null))
      .then((data) => {
        if (data) this.#productJsonCache[handle] = data;
        return data;
      })
      .catch(() => null);
  }

  #appendCardsToGrid(html) {
    const productGrid = this.querySelector('[ref="productGrid"]');
    if (!html || !productGrid) return;
    const parser = new DOMParser();
    const doc = parser.parseFromString(html, "text/html");
    const slides = doc.querySelectorAll(".swiper-slide");
    slides.forEach((slide) => {
      const item = slide.querySelector(".resource-list__item");
      if (item) productGrid.appendChild(item);
    });
  }

  /**
   * Handle the load more button click – fetch next page via Storefront API.
   */
  async #handleLoadMore() {
    if (this.#isLoading) return;

    const sectionId = this.dataset.sectionId;
    const collectionHandle = this.dataset.collectionHandle;
    const token = (typeof window !== "undefined" && window.PW_STOREFRONT_TOKEN) || "";
    const loadMoreLimit = parseInt(this.dataset.loadMoreLimit || "10", 10);
    const endCursor = this.dataset.endCursor || "";
    const hasNextPage = this.dataset.hasNextPage === "true";

    if (!sectionId || !collectionHandle || !token || !endCursor || !hasNextPage) {
      this.#hideButton();
      return;
    }

    const query = typeof window !== "undefined" && window.__loadMoreGridQueries && window.__loadMoreGridQueries[sectionId];
    if (!query || typeof CustomProductListCards === "undefined") {
      console.error("Load More Product Grid: missing query or CustomProductListCards");
      return;
    }

    this.#setLoading(true);

    try {
      const variables = {
        handle: collectionHandle,
        first: loadMoreLimit,
        after: endCursor,
      };

      const res = await fetch("/api/2025-01/graphql.json", {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          "X-Shopify-Storefront-Access-Token": token,
        },
        body: JSON.stringify({ query, variables }),
        signal: this.#abortController.signal,
      });

      if (!res.ok) {
        const text = await res.text();
        throw new Error(res.status + " " + text.slice(0, 200));
      }

      const result = await res.json();
      if (result.errors) throw new Error(result.errors.map((e) => e.message).join(", "));

      const productsData = result.data?.collection?.products;
      if (!productsData) throw new Error("No products in response");

      const edges = productsData.edges || [];
      const pageInfo = productsData.pageInfo || {};

      this.dataset.endCursor = pageInfo.endCursor || "";
      this.dataset.hasNextPage = pageInfo.hasNextPage ? "true" : "false";

      if (!pageInfo.hasNextPage) this.#hideButton();

      if (edges.length === 0) return;

      const quickAddColorScheme = this.dataset.quickAddColorScheme || "";
      const productTransition = this.dataset.productTransition || "";

      const html = await CustomProductListCards.buildProductCardsHTML(edges, {
        sectionId,
        quickAddColorScheme,
        productTransition,
        templateSuffix: document.body.getAttribute("data-pw-template-suffix") || "",
        getProductJson: this.#getProductJson.bind(this),
      });

      if (html) {
        this.#appendCardsToGrid(html);
        CustomProductListCards.initLazyCardGalleries(this.querySelector('[ref="productGrid"]'));
      }

      this.dispatchEvent(
        new CustomEvent("products-loaded", {
          bubbles: true,
          detail: { count: edges.length },
        })
      );
    } catch (error) {
      if (error.name === "AbortError") return;
      console.error("Error loading more products:", error);
      this.#setError("Failed to load products. Please try again.");
    } finally {
      this.#setLoading(false);
    }
  }

  #setLoading(isLoading) {
    this.#isLoading = isLoading;
    const button = this.querySelector('[ref="loadMoreButton"]');

    if (button) {
      if (isLoading) {
        setTimeout(()=>{
          this.setAttribute("loading", "");
          const loadingText = button.dataset.loadingText || "";
          button.dataset.originalHTML = button.innerHTML;
          button.innerHTML = loadingText;
          button.disabled = true;
        },0)
      } else {
        this.removeAttribute("loading");
        if (button.dataset.originalHTML) {
          button.innerHTML = button.dataset.originalHTML;
          delete button.dataset.originalHTML;
        }
        button.disabled = false;
      }
    }
  }

  #hideButton() {
    const buttonWrapper = this.querySelector(".load-more-product-grid__button-wrapper");
    if (buttonWrapper) {
      buttonWrapper.style.display = "none";
    }
  }

  #setError(message) {
    const button = this.querySelector('[ref="loadMoreButton"]');
    if (button) {
      if (!button.dataset.originalHTML) {
        button.dataset.originalHTML = button.innerHTML;
      }
      button.innerHTML = message;
      button.classList.add("error");
    }
  }
}

if (!customElements.get("load-more-product-grid")) {
  customElements.define("load-more-product-grid", LoadMoreProductGrid);
}
